import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button, Input, Label, Textarea } from "@/components/ui/primitives";
import { toast } from "@/components/ui/toaster";
import { cloneVersion } from "@/lib/api/endpoints";
import { qk } from "@/lib/query/keys";
import type { GenerationParams, VersionView } from "@/types/api";

/** `1.2.3` → `1.2.4`; anything that is not plain semver gets no suggestion. */
export function nextPatch(version: string): string {
  const m = /^(\d+)\.(\d+)\.(\d+)$/.exec(version.trim());
  return m ? `${m[1]}.${m[2]}.${Number(m[3]) + 1}` : "";
}

/**
 * Save the Playground's tuned settings as a new agent version (AVI-166).
 *
 * A full snapshot of `fromLabel` — prompts, model, tools, knowledge bases,
 * validators — with the tuned generation params in place of its own. The new
 * version starts as a draft, so nothing users chat with changes until it is
 * promoted.
 */
export function SaveVersionDialog({
  open,
  onOpenChange,
  agentSlug,
  fromLabel,
  fromVersion,
  generationParams,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agentSlug: string;
  fromLabel: string;
  /** The source's version string, used to suggest the next one. */
  fromVersion: string;
  /** The tuned params; null saves "provider defaults". */
  generationParams: GenerationParams | null;
  onSaved: (v: VersionView) => void;
}) {
  const qc = useQueryClient();
  const [version, setVersion] = React.useState("");
  const [note, setNote] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setVersion(nextPatch(fromVersion));
      setNote("");
    }
  }, [open, fromVersion]);

  const mutation = useMutation({
    mutationFn: () =>
      cloneVersion(agentSlug, fromLabel, {
        version: version.trim(),
        generation_params: generationParams,
        change_note: note.trim(),
      }),
    onSuccess: async (v) => {
      toast.success(`Saved as version ${v.version} (draft).`);
      await qc.invalidateQueries({ queryKey: qk.versions(agentSlug) });
      await qc.invalidateQueries({ queryKey: qk.myAgents });
      onOpenChange(false);
      onSaved(v);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const canSave = !!version.trim() && !!note.trim() && !mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save as new version</DialogTitle>
          <DialogDescription>
            Copies version {fromLabel} — prompts, model, tools, knowledge bases and validators —
            with these generation settings. It is saved as a draft; promote it when you are ready.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave) mutation.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="sv-version">Version</Label>
            <Input
              id="sv-version"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="1.0.1"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sv-note">Change note</Label>
            <Textarea
              id="sv-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="What changed and why — e.g. lower temperature, answers were drifting."
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSave}>
              {mutation.isPending ? "Saving…" : "Save version"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
